import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import moment from "moment"
import toast from "react-hot-toast"

import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectSearchable from "components/forms/select-searchable"

import useProductUnitRepositories from "repositories/product-unit"
import {
  useStockOpnameDetailRepositories,
  useStockCheckDetailRepositories,
} from "repositories/stock-opname"

import ByModelModal from "./by-model-modal"
import ImportModal from "./import-modal"

const formatNumber = (n) => Number(n || 0).toLocaleString("id-ID")

export default function CheckForm() {
  const { id, checkId } = useParams()
  const navigate = useNavigate()

  const { data: freezeData } = useStockOpnameDetailRepositories(id)
  const { data: check, isLoading, mutate: refetchCheck } = useStockCheckDetailRepositories(id, checkId)

  const [selectedProductId, setSelectedProductId] = useState("")
  const [selectedProductRow, setSelectedProductRow] = useState(null)
  const [quantity, setQuantity] = useState("")
  const [notesInput, setNotesInput] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [lastAdded, setLastAdded] = useState(null)

  const [productSearch, setProductSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const [checkDate, setCheckDate] = useState("")
  const [checkNotes, setCheckNotes] = useState("")
  const [savingMeta, setSavingMeta] = useState(false)

  const [itemSearch, setItemSearch] = useState("")
  const [showByModelModal, setShowByModelModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [exportingMode, setExportingMode] = useState(null)

  const qtyInputRef = useRef(null)

  useEffect(() => {
    if (check) {
      setCheckDate(check.check_date ? moment(check.check_date).format("YYYY-MM-DDTHH:mm") : "")
      setCheckNotes(check.notes || "")
    }
  }, [check?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(productSearch.trim()), 350)
    return () => clearTimeout(handle)
  }, [productSearch])

  useEffect(() => {
    if (selectedProductId && qtyInputRef.current) {
      qtyInputRef.current.focus()
      qtyInputRef.current.select?.()
    }
  }, [selectedProductId])

  const { data: productSelectionRaw, isLoading: isLoadingProductSelection } = useProductUnitRepositories({
    paginate: true,
    search: debouncedSearch || undefined,
  })

  const productOptions = useMemo(() => {
    if (Array.isArray(productSelectionRaw)) return productSelectionRaw
    if (Array.isArray(productSelectionRaw?.data)) return productSelectionRaw.data
    return []
  }, [productSelectionRaw])

  const freeze = freezeData?.freeze
  const status = String(freeze?.status || "").toUpperCase()
  const canEdit = status === "ACTIVE" || status === "REJECTED"

  const items = check?.items || []

  const totalQty = useMemo(
    () => items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0),
    [items]
  )

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase()
    if (!q) return items
    return items.filter((it) =>
      `${it.product_unit_name || ""} ${it.product_unit_id || ""}`.toLowerCase().includes(q)
    )
  }, [items, itemSearch])

  const addItem = async () => {
    if (!selectedProductId) {
      toast.error("Pilih produk dulu")
      return
    }
    const qty = Number(quantity)
    if (!Number.isFinite(qty) || qty < 0) {
      toast.error("Qty tidak valid")
      return
    }
    setSubmitting(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${checkId}/items`,
        {
          product_unit_id: selectedProductId,
          quantity: qty,
          notes: notesInput || undefined,
        }
      )
      const productLabel = selectedProductRow?.long_name || selectedProductRow?.name || selectedProductId
      setLastAdded({ name: productLabel, qty })
      toast.success(`+${qty} · ${productLabel}`)
      setSelectedProductId("")
      setSelectedProductRow(null)
      setQuantity("")
      setNotesInput("")
      setProductSearch("")
      await refetchCheck()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal menambah item")
    } finally {
      setSubmitting(false)
    }
  }

  const handleQtyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      if (selectedProductId && !submitting) addItem()
    }
  }

  const handleExport = async (mode) => {
    setExportingMode(mode)
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${checkId}/export`,
        { params: { mode }, responseType: "blob" }
      )
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const url = window.URL.createObjectURL(blob)
      const cd = res.headers?.["content-disposition"] || ""
      const m = cd.match(/filename="?([^"]+)"?/i)
      const filename = m?.[1] || `cek-stock-${mode}.xlsx`
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success("Excel terunduh")
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal ekspor Excel")
    } finally {
      setExportingMode(null)
    }
  }

  const updateItem = async (item, nextQty) => {
    const qty = Number(nextQty)
    if (!Number.isFinite(qty) || qty < 0) return
    if (qty === Number(item.quantity)) return
    try {
      await axios.put(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${checkId}/items/${item.id}`,
        { quantity: qty }
      )
      await refetchCheck()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal update item")
    }
  }

  const adjustQty = (item, delta) => {
    const next = Math.max(0, Number(item.quantity || 0) + delta)
    updateItem(item, next)
  }

  const deleteItem = async (item) => {
    if (!window.confirm(`Hapus item ${item.product_unit_name}?`)) return
    try {
      await axios.delete(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${checkId}/items/${item.id}`
      )
      toast.success("Item dihapus")
      await refetchCheck()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal hapus item")
    }
  }

  const saveMeta = async () => {
    setSavingMeta(true)
    try {
      await axios.put(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${checkId}`,
        {
          check_date: checkDate ? moment(checkDate).format("YYYY-MM-DD HH:mm:ss") : undefined,
          notes: checkNotes ?? undefined,
        }
      )
      toast.success("Cek stock diperbarui")
      await refetchCheck()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Gagal update cek stock")
    } finally {
      setSavingMeta(false)
    }
  }

  if (isLoading || !check) {
    return <div className="p-10 text-sm text-center text-neutral-500">Memuat cek stock...</div>
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-24">
      <div className="p-4 sm:p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-neutral-500">
          <Link to="/stock-opname" className="hover:underline">Stock Opname</Link>
          <span>/</span>
          <Link to={`/stock-opname/${id}`} className="hover:underline truncate">{freeze?.freeze_no}</Link>
          <span>/</span>
          <span className="truncate">{check.check_no}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-lg font-semibold text-neutral-900">{check.check_no}</h2>
          <span className="text-sm text-neutral-500">
            {items.length} item · total {formatNumber(totalQty)} qty
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 mt-4 md:grid-cols-3">
          <div>
            <Label value="Tanggal Cek" />
            <Input
              type="datetime-local"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div className="md:col-span-2">
            <Label value="Catatan" />
            <Input
              type="text"
              value={checkNotes}
              onChange={(e) => setCheckNotes(e.target.value)}
              disabled={!canEdit}
              placeholder="Catatan cek stock (opsional)"
            />
          </div>
        </div>

        {canEdit && (
          <div className="mt-3">
            <button
              type="button"
              onClick={saveMeta}
              disabled={savingMeta}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg bg-brand-900 disabled:opacity-50"
            >
              {savingMeta ? "Menyimpan..." : "Simpan Header"}
            </button>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="p-4 sm:p-5 bg-white border rounded-2xl border-neutral-200 sm:sticky sm:top-2 sm:z-20 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-900">Tambah Item</h3>
            <div className="flex flex-wrap items-center gap-2">
              {lastAdded && (
                <span className="text-xs text-emerald-700 truncate max-w-[180px] sm:max-w-none">
                  ✓ +{formatNumber(lastAdded.qty)} {lastAdded.name}
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowByModelModal(true)}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 border rounded-lg bg-blue-50 border-blue-200 hover:bg-blue-100"
              >
                + per Model
              </button>
              <button
                type="button"
                onClick={() => handleExport("template")}
                disabled={exportingMode !== null}
                className="px-3 py-1.5 text-xs font-medium text-emerald-700 border rounded-lg bg-emerald-50 border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
                title="Unduh Excel template berisi semua produk di outlet"
              >
                {exportingMode === "template" ? "..." : "↓ Template"}
              </button>
              <button
                type="button"
                onClick={() => handleExport("current")}
                disabled={exportingMode !== null}
                className="px-3 py-1.5 text-xs font-medium text-emerald-700 border rounded-lg bg-emerald-50 border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
                title="Unduh Excel berisi item yang sudah ada di cek ini"
              >
                {exportingMode === "current" ? "..." : "↓ Saat Ini"}
              </button>
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="px-3 py-1.5 text-xs font-medium text-amber-700 border rounded-lg bg-amber-50 border-amber-200 hover:bg-amber-100"
              >
                ↑ Import Excel
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            Pilih produk → input qty → Enter atau tap Tambah. Produk yang sama akan di-SUM.
          </p>

          <div className="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-12 sm:items-end">
            <div className="sm:col-span-12 md:col-span-6">
              <Label value="Produk *" />
              <SelectSearchable
                selection={productOptions}
                isLoading={isLoadingProductSelection}
                value={selectedProductId}
                onChange={(value, row) => {
                  setSelectedProductId(String(value || ""))
                  setSelectedProductRow(row || null)
                }}
                keyValue={(v) => v.id}
                title={(v) => v.long_name || v.name}
                description={(v) => [v.sku, v.product_model?.name].filter(Boolean).join(" · ")}
                placeholder="Cari nama / SKU produk..."
                onSearchChange={(q) => setProductSearch(String(q || ""))}
                serverSearch
                clearOnClose={false}
              />
            </div>
            <div className="sm:col-span-5 md:col-span-2">
              <Label value="Qty Fisik *" />
              <input
                ref={qtyInputRef}
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onKeyDown={handleQtyKeyDown}
                placeholder="0"
                className="block w-full px-3 py-3 text-base font-medium text-right border rounded-xl border-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-900"
              />
            </div>
            <div className="sm:col-span-4 md:col-span-2">
              <Label value="Catatan" />
              <Input
                type="text"
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                placeholder="Opsional"
              />
            </div>
            <div className="sm:col-span-3 md:col-span-2">
              <button
                type="button"
                onClick={addItem}
                disabled={submitting || !selectedProductId}
                className="w-full px-4 py-3 text-base font-semibold text-white rounded-xl bg-brand-900 hover:bg-brand-800 disabled:opacity-50"
              >
                {submitting ? "..." : "+ Tambah"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-2xl border-neutral-200 overflow-hidden">
        <div className="flex flex-col gap-3 px-4 sm:px-5 py-4 border-b border-neutral-200 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-neutral-900">
            Item ({items.length}) · Total {formatNumber(totalQty)} qty
          </h3>
          {items.length > 0 && (
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Filter item..."
              className="w-full sm:w-64 px-3 py-2 text-sm border rounded-lg border-neutral-300"
            />
          )}
        </div>

        {items.length === 0 ? (
          <div className="p-10 text-sm text-center text-neutral-500">
            Belum ada item di cek stock ini.
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-sm text-center text-neutral-500">
            Tidak ada item yang cocok dengan filter "{itemSearch}".
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="divide-y divide-neutral-100 sm:hidden">
              {filteredItems.map((it, idx) => (
                <li key={it.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-neutral-400">
                        <span>#{idx + 1}</span>
                      </div>
                      <div className="font-medium text-neutral-900 break-words">{it.product_unit_name}</div>
                      <div className="text-xs text-neutral-500">{it.product_unit_id}</div>
                      {it.notes && <div className="mt-1 text-xs text-neutral-600 italic">"{it.notes}"</div>}
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => deleteItem(it)}
                        className="shrink-0 px-2 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg"
                      >
                        Hapus
                      </button>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    {canEdit ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => adjustQty(it, -1)}
                          className="w-10 h-10 flex items-center justify-center text-lg font-bold text-neutral-700 border rounded-xl border-neutral-300 bg-white active:bg-neutral-100"
                        >
                          −
                        </button>
                        <input
                          // key include quantity → force remount saat qty berubah
                          // (mis. dari +/-, import excel, by-model picker). Tanpa ini,
                          // input pakai defaultValue lama karena uncontrolled component.
                          key={`${it.id}-${it.quantity}`}
                          type="number"
                          inputMode="numeric"
                          min="0"
                          step="1"
                          defaultValue={it.quantity}
                          onBlur={(e) => updateItem(it, Number(e.target.value))}
                          className="w-20 px-2 py-2 text-center text-lg font-semibold border rounded-xl border-neutral-300"
                        />
                        <button
                          type="button"
                          onClick={() => adjustQty(it, 1)}
                          className="w-10 h-10 flex items-center justify-center text-lg font-bold text-neutral-700 border rounded-xl border-neutral-300 bg-white active:bg-neutral-100"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <div className="text-lg font-semibold text-neutral-900">{formatNumber(it.quantity)}</div>
                    )}
                    <div className="text-xs text-neutral-500">qty fisik</div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50">
                  <tr className="text-left text-neutral-600">
                    <th className="px-5 py-3 font-medium w-12">#</th>
                    <th className="px-5 py-3 font-medium">Produk</th>
                    <th className="px-5 py-3 font-medium text-right w-48">Qty Fisik</th>
                    <th className="px-5 py-3 font-medium">Catatan</th>
                    {canEdit && <th className="px-5 py-3 font-medium text-right w-24">Aksi</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((it, idx) => (
                    <tr key={it.id} className="border-t border-neutral-100 hover:bg-brand-50 transition-colors">
                      <td className="px-5 py-3 text-neutral-500">{idx + 1}</td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-neutral-900">{it.product_unit_name}</div>
                        <div className="text-xs text-neutral-500">{it.product_unit_id}</div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {canEdit ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => adjustQty(it, -1)}
                              className="w-8 h-8 flex items-center justify-center text-base font-bold text-neutral-700 border rounded-lg border-neutral-300 bg-white hover:bg-neutral-100"
                            >
                              −
                            </button>
                            <input
                              // key include quantity → force remount saat qty berubah
                              // (mis. dari +/-, import excel, by-model picker). Tanpa ini,
                              // input pakai defaultValue lama karena uncontrolled component.
                              key={`${it.id}-${it.quantity}`}
                              type="number"
                              inputMode="numeric"
                              min="0"
                              step="1"
                              defaultValue={it.quantity}
                              onBlur={(e) => updateItem(it, Number(e.target.value))}
                              className="w-20 px-2 py-1.5 text-center font-medium border rounded-lg border-neutral-300"
                            />
                            <button
                              type="button"
                              onClick={() => adjustQty(it, 1)}
                              className="w-8 h-8 flex items-center justify-center text-base font-bold text-neutral-700 border rounded-lg border-neutral-300 bg-white hover:bg-neutral-100"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <span className="font-medium">{formatNumber(it.quantity)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-neutral-700">{it.notes || "-"}</td>
                      {canEdit && (
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => deleteItem(it)}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
                          >
                            Hapus
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-neutral-50">
                  <tr className="border-t border-neutral-200">
                    <td className="px-5 py-3" colSpan={2}>
                      <strong>Total</strong>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">{formatNumber(totalQty)}</td>
                    <td className="px-5 py-3" colSpan={canEdit ? 2 : 1}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => navigate(`/stock-opname/${id}`)}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-neutral-700 border rounded-lg border-neutral-300 bg-white"
        >
          Kembali ke Freeze
        </button>
      </div>

      {showByModelModal && canEdit && (
        <ByModelModal
          freezeId={id}
          checkId={checkId}
          onClose={() => setShowByModelModal(false)}
          onSuccess={() => refetchCheck()}
        />
      )}

      {showImportModal && canEdit && (
        <ImportModal
          freezeId={id}
          checkId={checkId}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => refetchCheck()}
        />
      )}
    </div>
  )
}
